import { useEffect, useState } from "react";
import { Lightning, Gif, Terminal } from "@/components/ui/icons";
import { useAuth } from "@workos-inc/authkit-react";
import { getAccessTokenWithRetry } from "@/lib/auth-token";
import { listSkills } from "@/api/skills";

export type SlashCommand = {
  command: string;
  label: string;
  description: string;
  icon: typeof Gif;
  color: string;
};

export const SLASH_COMMANDS: SlashCommand[] = [
  {
    command: "giphy",
    label: "Search GIFs",
    description: "Search for and share a GIF",
    icon: Gif,
    color: "#00e676",
  },
  {
    command: "run",
    label: "Run Command",
    description: "Run a shell command and share output with the channel",
    icon: Terminal,
    color: "#38bdf8",
  },
];

export function useSlashCommands(workspaceId?: string): SlashCommand[] {
  const { getAccessToken } = useAuth();
  const [customCommands, setCustomCommands] = useState<SlashCommand[]>([]);

  useEffect(() => {
    if (!workspaceId) return;
    const wsId = workspaceId;

    let cancelled = false;

    async function loadCustomCommands() {
      try {
        const token = await getAccessTokenWithRetry(getAccessToken, {
          maxWaitMs: 3000,
          intervalMs: 200,
        });
        if (!token || cancelled) return;
        const data = await listSkills({
          accessToken: token,
          workspaceId: wsId,
          triggerType: "slash_command",
        });
        if (cancelled) return;
        setCustomCommands(
          data.skills.map((skill) => {
            const triggerCfg = skill.trigger_config as
              | { command?: string }
              | undefined;
            return {
              command: triggerCfg?.command ?? skill.slug,
              label: skill.name,
              description: skill.description ?? "Custom skill",
              icon: Lightning,
              color: "#a78bfa",
            };
          }),
        );
      } catch {
        // silently fail
      }
    }

    void loadCustomCommands();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, getAccessToken]);

  return [...SLASH_COMMANDS, ...customCommands];
}
